import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { SKILL_ADVICE, SKILL_LABELS, type Answer, type TakerQuestion } from '@ready/shared';
import { useApi, type AnswerFeedback, type StartedAttempt } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/me';
import { NameForm } from '@/components/name-form';
import { Badge, Button, Card, ErrorText } from '@/components/ui';
import { ShareQuizCard } from '@/components/quiz/share-quiz';

/**
 * Taking a quiz: intro, one question at a time, then results. Anyone can
 * read the intro (share links), but starting needs an account, and quizzes
 * with certificates need the learner's name on file. Self-assessments show
 * feedback after each answer; assessments only at the end.
 */
export function TakeQuizPage() {
  const { slug = '' } = useParams();
  const api = useApi();
  const { user, loading } = useAuth();
  const me = useMe();
  const info = useQuery({
    queryKey: ['publicQuiz', slug],
    queryFn: () => api.publicQuiz(slug),
    retry: false,
  });
  const start = useMutation({ mutationFn: () => api.startAttempt(slug) });
  // "Start" on Home links here with ?start=1 to skip the intro when nothing is needed first.
  const [params, setParams] = useSearchParams();
  const autoStart = params.get('start') === '1';
  const canStart =
    Boolean(user) &&
    info.isSuccess &&
    me.isSuccess &&
    !(info.data.certificate && !me.data.displayName);
  useEffect(() => {
    if (!autoStart || !canStart || !start.isIdle) return;
    // Drop ?start=1 so a refresh mid-quiz doesn't use up another attempt.
    setParams({}, { replace: true });
    start.mutate();
  }, [autoStart, canStart, start, setParams]);

  if (info.isPending || loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (info.isError) return <ErrorText error={info.error} />;
  if (start.data) return <Player attempt={start.data} />;

  const q = info.data;
  // Certificate quizzes print the learner's name, so it must be on file first.
  const needsName = Boolean(user && q.certificate && me.data && !me.data.displayName);

  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <p className="text-sm text-muted-foreground">{q.issuerName}</p>
        <h1 className="mt-1 text-2xl font-semibold">{q.title}</h1>
        {q.description && <p className="mt-3 text-muted-foreground">{q.description}</p>}
        <ul className="mt-4 flex flex-wrap gap-2">
          <Badge>{q.questionCount} questions</Badge>
          <Badge>
            {q.mode === 'self_assessment' ? 'Feedback after each answer' : 'Results at the end'}
          </Badge>
          {q.certificate && <Badge>Certificate at {q.passPercent}%</Badge>}
          {q.maxAttempts && <Badge>{q.maxAttempts} attempts</Badge>}
        </ul>

        {!user ? (
          <div className="mt-6 space-y-2">
            <Link to={`/signin?next=${encodeURIComponent(`/q/${slug}`)}`}>
              <Button className="w-full">Sign in to start</Button>
            </Link>
            <p className="text-center text-xs text-muted-foreground">
              Free account, no password: we email you a code.
            </p>
          </div>
        ) : needsName && me.data ? (
          <div className="mt-6 rounded-lg bg-muted p-4">
            <p className="mb-3 font-medium">Add your name for your certificate</p>
            <NameForm me={me.data} submitLabel="Save and continue" />
          </div>
        ) : (
          <>
            <Button
              className="mt-6 w-full"
              disabled={start.isPending || me.isPending}
              onClick={() => start.mutate()}
            >
              Start
            </Button>
            {q.certificate && me.data?.displayName && (
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Your certificate will be issued to{' '}
                <span className="font-medium text-foreground">{me.data.displayName}</span>.{' '}
                <Link to="/account" className="text-primary">
                  Change
                </Link>
              </p>
            )}
          </>
        )}
        <ErrorText error={start.error} />
      </Card>
      <div className="mt-6">
        <ShareQuizCard
          slug={slug}
          audience={q.audience}
          heading="Help others get ready"
          text={
            q.audience === 'public'
              ? 'Share this challenge with friends, classmates and colleagues, or run a campaign with printable posters for notice boards and common areas.'
              : 'Run a campaign in your organization with printable posters for notice boards and common areas.'
          }
        />
      </div>
    </div>
  );
}

function Player({ attempt }: { attempt: StartedAttempt }) {
  const api = useApi();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [feedback, setFeedback] = useState<Record<string, AnswerFeedback>>({});
  const selfAssessment = attempt.quiz.mode === 'self_assessment';
  const q = attempt.questions[index]!;
  const chosen = answers[q.id] ?? [];
  const fb = feedback[q.id];
  const last = index === attempt.questions.length - 1;

  const check = useMutation({
    mutationFn: (answer: Answer) => api.checkAnswer(attempt.attemptId, answer),
    onSuccess: (r) => setFeedback((f) => ({ ...f, [q.id]: r })),
  });
  const submit = useMutation({
    mutationFn: () =>
      api.submitAttempt(
        attempt.attemptId,
        Object.entries(answers).map(([questionId, optionIds]) => ({ questionId, optionIds })),
      ),
    onSuccess: () => navigate(`/attempts/${attempt.attemptId}`, { replace: true }),
  });

  const toggle = (optionId: string) => {
    if (fb) return;
    setAnswers((a) => ({
      ...a,
      [q.id]:
        q.type === 'multiple'
          ? chosen.includes(optionId)
            ? chosen.filter((x) => x !== optionId)
            : [...chosen, optionId]
          : [optionId],
    }));
  };

  const next = () => (last ? submit.mutate() : setIndex((i) => i + 1));

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
        <span>{attempt.quiz.title}</span>
        <span className="tabular-nums">
          {index + 1} of {attempt.questions.length}
        </span>
      </div>
      <progress
        max={attempt.questions.length}
        value={index + 1}
        aria-hidden
        className="mb-4 h-1 w-full appearance-none overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
      />
      <Card>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {SKILL_LABELS[q.skill]}
        </p>
        <h2 className="mt-2 text-lg font-semibold">{q.prompt}</h2>
        {q.type === 'multiple' && (
          <p className="mt-1 text-sm text-muted-foreground">Choose all that apply.</p>
        )}
        <Options question={q} chosen={chosen} feedback={fb} onToggle={toggle} />

        {fb && (
          <div
            className={`mt-4 rounded-lg p-3 text-sm ${fb.correct ? 'bg-india-green/10' : 'bg-destructive/10'}`}
          >
            <p className="flex items-center gap-2 font-medium">
              {fb.correct ? (
                <CheckCircle2 className="size-4 text-india-green" aria-hidden />
              ) : (
                <XCircle className="size-4 text-destructive" aria-hidden />
              )}
              {fb.correct ? 'Correct' : 'Not quite'}
            </p>
            <p className="mt-1 text-muted-foreground">
              {fb.correct ? fb.explanation : `Tip: ${SKILL_ADVICE[fb.skill]}`}
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          {selfAssessment && !fb ? (
            <Button
              disabled={!chosen.length || check.isPending}
              onClick={() => check.mutate({ questionId: q.id, optionIds: chosen })}
            >
              Check answer
            </Button>
          ) : (
            <Button
              disabled={(!selfAssessment && !chosen.length) || submit.isPending}
              onClick={next}
            >
              {last ? 'See results' : 'Next question'}
            </Button>
          )}
        </div>
        <ErrorText error={check.error ?? submit.error} />
      </Card>
    </div>
  );
}

function Options({
  question,
  chosen,
  feedback,
  onToggle,
}: {
  question: TakerQuestion;
  chosen: string[];
  feedback?: AnswerFeedback;
  onToggle: (id: string) => void;
}) {
  return (
    <div role={question.type === 'multiple' ? 'group' : 'radiogroup'} className="mt-4 space-y-2">
      {question.options.map((o) => {
        const selected = chosen.includes(o.id);
        // Feedback marks only the learner's own choice; the right option is never revealed.
        const state =
          feedback && selected
            ? feedback.correct
              ? 'right'
              : 'wrong'
            : selected
              ? 'selected'
              : 'idle';
        return (
          <button
            key={o.id}
            type="button"
            role={question.type === 'multiple' ? 'checkbox' : 'radio'}
            aria-checked={selected}
            disabled={Boolean(feedback)}
            onClick={() => onToggle(o.id)}
            className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
              state === 'selected'
                ? 'border-primary bg-primary/10'
                : state === 'right'
                  ? 'border-india-green bg-india-green/10'
                  : state === 'wrong'
                    ? 'border-destructive bg-destructive/10'
                    : 'border-border hover:bg-muted'
            }`}
          >
            {o.text}
          </button>
        );
      })}
    </div>
  );
}
